<template>
  <v-layout :title="$t('transaction_checkout.confirm_order')">
    <vue-element-loading :active="loading || active_cart_is_busy" />
    <b-row v-if="loaded && !active_cart_is_busy">
      <b-col cols="12" order="2" md="8" order-md="1">
        <div class="mb-5">
          <h4 class="border-bottom mb-3 pb-2 mt-4 mt-md-0 text-uppercase">
            {{ $t("transaction_checkout.delivery") }}
          </h4>
          <ShoppingCartDelivery
            :deliveryAddressUuid="deliveryAddressUuid"
            @input="onDeliveryChange"
            v-on:address-click="onAddressClick"
            :filter_address_by_current_country="
              filter_address_by_current_country
            "
          />
        </div>

        <div class="mb-5" v-if="documentation && documentation.documents && documentation.documents.length > 0">
          <h4 class="border-bottom mb-3 pb-2 mt-4 mt-md-0 text-uppercase">
            {{ $t("transaction_checkout.documents_title") }}
          </h4>
          <VDocumentCardList
            :documents="documentation.documents"
          />
        </div>

        <b-alert
          v-if="!validated_delivery_address_to_country"
          show
          variant="warning"
        >
          {{ $t("transaction_checkout.select_address_to_country") }}

        </b-alert>
        <div v-if="loading_delivery_options">
          <vue-element-loading :active="loading_delivery_options" />
        </div>
        <div
          v-if="
            !loading_delivery_options && validated_delivery_address_to_country
          "
        >
          <h4 class="border-bottom mb-3 mt-5 pb-2 text-uppercase">
            {{ $t("transaction_checkout.checkout") }}
          </h4>
          <b-card class="border disabled">
            <b-container fluid>
              <vue-element-loading :active="loading_main_transaction" />
              <vue-element-loading
                :active="(pre_loading && !loading)"
              />
              <CheckoutControl
                v-for="partial_tr in checkout_by_pharmacy"
                :key="partial_tr.organization_uuid"
                :transaction="partial_tr"
                :documentation_uuid="documentation.uuid"
                :checkout_uuid="checkout_uuid"
                @carrier-select="onCarrierChange"
                handle_drug_recipe_file
                :has_payment_intention="
                  Boolean(hasPaymentIntention[partial_tr.organization_uuid])
                "
              >
              </CheckoutControl>
            </b-container>
          </b-card>
        </div>
      </b-col>

      <b-col cols="12" order="1" md="4" order-md="2">
        <TotalAmountSummary
          :amount="computed_amount"
          :showAll="true"
          :title="$t('transaction_checkout.order_summary')"
          :items="global_items"
          class="order_summary pt-5 pt-md-0"
        />
      </b-col>
    </b-row>
  </v-layout>
</template>

<script>
// Componente que hace split por farmacia, es el componente principal
// de la pantalla de pagos.
// El componente TotalAmountSummary muestra el total sumado de todas
// las farmacias
// El componente CheckoutControl contiene los datos de productos, transporte y
// pago de cada farmacia, a éste se le van pasando los datos de cada farmacia
// dentro de un v-for

import { mapGetters, mapActions } from "vuex";
import { mapState } from "vuex";
import VLayout from "@/layouts/Default";
import ShoppingCartDelivery from "@/components/ShoppingCartDelivery";
import VDocumentCardList from "@/components/VDocumentCardList";

import CheckoutControl from "@/components/CheckoutControl";
import TotalAmountSummary from "@/components/TotalAmountSummary";
import { Amount } from "@/lib/amount.js";

export default {
  name: "import-medicines-payment",
  components: {
    VLayout,
    ShoppingCartDelivery,
    CheckoutControl,
    TotalAmountSummary,
    VDocumentCardList,
  },
  props: {
    filter_address_by_current_country: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      loading_main_transaction: false,
      pre_loading: false,
      loading_delivery_options: false,
      loading_payment_options: false,
      loaded: false,
      purchase_options: undefined,
      deliveryMethods: [],
      load_data_timeout: undefined,
    };
  },
  computed: {
    ...mapGetters("auth", ["deviceCountry"]),
    ...mapState({
      global_amount: (state) => state.checkout.global_amount,
      global_items: (state) => state.checkout.global_items,
      checkout_by_pharmacy: (state) => state.checkout.checkout_by_pharmacy,
      delivery_address: (state) => state.checkout.delivery_address,
      documentation: (state) => state.checkout.documentation,
      active_cart_is_busy: (state) => state.shoppingcart.active_cart_is_busy,
      payment_intentions: (state) => state.drugtransaction.payment_intentions,
      mainpatient: (state) => state.naturalperson.mainpatient,
    }),
    validated_delivery_address_to_country() {
      if (!this.filter_address_by_current_country) {
        return true;
      }
      if (!this.delivery_address) {
        return true;
      }
      if (this.delivery_address.country == this.deviceCountry) {
        return true;
      }
      return false;
    },
    checkout_uuid() {
      return this.$route.params.checkout_uuid;
    },
    computed_amount: function () {
      if (!this.global_amount) {
        return undefined;
      }
      return new Amount(this.global_amount);
    },
    deliveryAddressUuid() {
      // Dirección de entrega para el carrito
      if (this.delivery_address) {
        return this.delivery_address.address_uuid;
      }
      return undefined;
    },
    hasPaymentIntention() {
      if (!this.payment_intentions) {
        return {};
      }
      var intentions_by_pharmacy_uuid = {};
      this.payment_intentions.forEach((element) => {
        intentions_by_pharmacy_uuid[element.pharmacy_uuid] = element;
      });
      return intentions_by_pharmacy_uuid;
    },
  },
  mounted() {
    if (this.mainpatient) {
      clearTimeout(this.load_data_timeout);
      this.loadData();
    } else {
      this.$router.push({
        name: "patientdata-to-cart",
      });
    }
  },
  watch: {
    active_cart_is_busy: function (isBusy, prevStatus) {
      if (!isBusy && isBusy != prevStatus) {
        this.loaded = false;
        clearTimeout(this.load_data_timeout);
        this.loadData();
      }
    },
  },
  destroyed: function () {
    clearInterval(this.refresh_payment_intentions_interval);
  },
  methods: {
    ...mapActions({
      LIST_PAYMENT_INTENTIONS: "drugtransaction/LIST_PAYMENT_INTENTIONS",
      REQUEST_CHECKOUT: "checkout/REQUEST_CHECKOUT",
      REQUEST_UPDATE_CHECKOUT: "checkout/REQUEST_UPDATE_CHECKOUT",
    }),
    loadData: function () {
      this.loading = true;
      if (this.active_cart_is_busy) {
        this.load_data_timeout = setTimeout(() => this.loadData(), 500);
      } else {
        this.loadMainTransaction();
      }
    },
    refreshPaymentIntentions: function () {
      // sólo refrescará las intenciones de pago existentes
      // if (!this.payment_intentions || this.payment_intentions.length <= 0) {
      //   return;
      // }
      return this.LIST_PAYMENT_INTENTIONS().then(undefined, (err) => {
        if (err.response && err.response.status === 401) {
          // Por alguna razón hemos dejado de estar autenticados
          this.$router.push({ name: "login", params: { backto: this.$route } });
        }
      });
    },
    // onPaymentIntention: function(intention) {
    // },
    // hasPaymentIntention: function(pharmacy_uuid) {
    //   if (!this.payment_intentions) {
    //     return false;
    //   }
    //   let filtered_payments = this.payment_intentions.filter(p => {
    //     // Search seller participant
    //     return p.pharmacy_uuid == pharmacy_uuid;
    //   });
    //   if (filtered_payments.length > 0) {
    //     return true;
    //   }
    //   return false;
    // },
    getAmountObject: function (raw_amount) {
      return new Amount(raw_amount);
    },
    loadMainTransaction() {
      this.pre_loading = false;
      this.loading_main_transaction = true;
      this.LIST_PAYMENT_INTENTIONS().then(
        () => {
          this.REQUEST_CHECKOUT({
            checkout_uuid: this.checkout_uuid,
          }).then(
            () => {
              this.refresh_payment_intentions_interval = setInterval(
                function () {
                  this.refreshPaymentIntentions();
                }.bind(this),
                // every 10 seconds
                10000
              );

              this.loading_main_transaction = false;
              this.loading = false;
              this.loaded = true;
            },
            (err) => {
              this.loading_main_transaction = false;
              this.loading = false;
              this.$sentry({ capture: err });
              this.mnotify_error({
                text: this.$t("transaction_checkout.error.load_cart"),
              });
            }
          );
        },
        (err) => {
          this.loading_main_transaction = false;
          this.loading = false;
          this.$sentry({ capture: err });
          this.mnotify_error({
            text: this.$t("transaction_checkout.error.load_cart"),
          });
        }
      );
    },
    // al cambiar transportista
    onCarrierChange(carrier_delivery) {
      let data = {
        set_carrier_delivery: carrier_delivery.uuid,
        set_carrier_delivery_organization: carrier_delivery.pharmacy_uuid,
      };
      if (carrier_delivery.external_service_id) {
        data["set_carrier_delivery_external_service_id"] =
          carrier_delivery.external_service_id;
      }
      this.pre_loading = true;
      this.REQUEST_UPDATE_CHECKOUT({
        checkout_uuid: this.checkout_uuid,
        data: data,
      }).then(
        () => {
          this.pre_loading = false;
        },
        () => {
          this.pre_loading = false;
        }
      );
    },
    // al cambiar dirección de envío
    onDeliveryChange(delivery) {
      if (!delivery.addressUuid) {
        // esto tendremos que cambiarlo cuando funcione lo de recogida
        // en farmacia
        return;
      }

      this.pre_loading = true;
      this.loading_delivery_options = true;
      this.REQUEST_UPDATE_CHECKOUT({
        checkout_uuid: this.checkout_uuid,
        data: {
          set_delivery_address: String(delivery.addressUuid),
        },
      }).then(
        () => {
          this.pre_loading = false;
          this.loading_delivery_options = false;
        },
        () => {
          this.pre_loading = false;
          this.loading_delivery_options = false;
        }
      );
    },
    onAddressClick() {
      this.pre_loading = true;
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        transaction_checkout: {
          delivery: "Delivery",
          delivery_blocked_message:
            "Please, select one address to show delivery options",
          payment_blocked_message:
            "Please, select one delivery address to show payment options",
          checkout: "Checkout",
          send_to_address: "Send to this address",
          confirm_order: "Order confirmation",
          payment_method_title: "3- Payment",
          error: {
            load_cart: "Cannot load shopping cart",
            set_delivery_address: "Cannot set Delivery address",
            request_delivery_methods: "Cannot load delivery methods",
          },
          order_summary: "Order summary",
          documents_title: "Documents",
          select_address_to_country:
            "Please, select a valid address to your current country",
        },
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.order_summary {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
}
</style>
